import {AfterViewInit, Component, OnDestroy, ViewChild} from '@angular/core';
import {ReplaySubject} from 'rxjs';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {IddTrainingService} from '../../../shared/services/idd-training.service';
import {AuthService} from '../../../shared/services/auth.service';
import {MatDialog} from '@angular/material/dialog';
import {delay, distinctUntilChanged, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../shared/interface/response/counted-and-paginated';
import {IddTrainingCertificate} from '../../../shared/interface/idd-training-certificate';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import {MatSnackBar} from '@angular/material/snack-bar';
import {CertificateOverviewDialogComponent} from '../certificate-overview-dialog/certificate-overview-dialog.component';
import {Uuid} from '../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../shared/components/job-result-dialog/job-result-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {Sort} from '@angular/material/sort';
import {ActivatedRoute, Router} from '@angular/router';
import {FilterService} from '../../../shared/services/filter.service';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../shared/ngrx/reducers';
import {getCurrentRouteState} from '../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../shared/ngrx/route-serializer';

@Component({
  selector: 'app-all-idd-training-certificates-list',
  templateUrl: './all-idd-training-certificates-list.component.html',
  styleUrls: ['./all-idd-training-certificates-list.component.scss']
})
export class AllIddTrainingCertificatesListComponent implements AfterViewInit, OnDestroy {
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  initialFilters: any;
  iddTrainingCertificates: IddTrainingCertificate[] = [];
  displayedColumns: string[] = ['iddTraining', 'rau', 'partner', 'ofwcaAssetsRegional', 'partnerAssetsRegional',
    'meetingLeader', 'aml', 'insuranceCompany', 'realisedHours', 'realisedAt', 'createdAt', 'createdBy', 'actions', ];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  sortBy = 'realisedAt';
  sortOrder = 'desc';

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private iddTrainingService: IddTrainingService,
    private auth: AuthService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.iddTrainingService.iddTrainingCertificateListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<IddTrainingCertificate>) => {
          this.iddTrainingCertificates = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      year: 'string',
      ofwca: 'string',
      partner: 'string',
      assetsRegional: 'string',
      status: 'int',
      meetingLeader: 'int',
      insuranceCompany: 'int',
      gte$realisedAt: 'date',
      lte$realisedAt: 'date',
      rauNumber: 'string',
      createdBy: 'string',
      aml: 'int',
      gte$createdAt: 'date',
      lte$createdAt: 'date',
    }, filters);
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: event.pageIndex,
          pageSize: event.pageSize,
          ...this.filtersForm.value
        })]);
      });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.sortBy = state.params?.sortBy ? state.params.sortBy : 'realisedAt';
        this.sortOrder = state.params?.sortOrder ? state.params.sortOrder : 'desc';
        this.loadListData();
      })
    ;
  }

  sortChanged($event: Sort): void {
    this.sortBy = $event.active;
    this.sortOrder = $event.direction;

    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      sortBy: $event.active,
      sortOrder: $event.direction,
      page: this.paginator.pageIndex,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value
    })]);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.iddTrainingService.loadCertificateList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.initialFilters,
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      }
    );
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  downloadReport(): void {
    this.iddTrainingService.downloadCertificateReport(this.filtersForm.value)
      .subscribe((response: Uuid) => {
        const dialogRef = this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  downloadCertificate(certificate: IddTrainingCertificate): void {
    this.iddTrainingService.downloadCertificate(certificate.uuid);
  }

  deleteCertificate(certificate: IddTrainingCertificate): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć zaświadczenie?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.iddTrainingService.deleteCertificate(certificate.uuid)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto załącznik', '', {duration: 2000});
            this.loadListData();
          });
      }
    });
  }

  floor(num: number): number {
    return Math.floor(num);
  }

  certificateOverview(certificate: IddTrainingCertificate): void {
    this.dialog.open(CertificateOverviewDialogComponent, {
      width: '60vw',
      height: '90vh',
      autoFocus: false,
      data: {
        certificateId: certificate.uuid,
        mode: 'ASF24'
      },
    });
  }

  generateCertificates(): void {
    this.iddTrainingService.generateCertificateListArchive(this.filtersForm.value)
      .subscribe((response: Uuid) => {
        const dialogRef = this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });

        dialogRef.afterClosed().subscribe(result => {
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }
}
